import { Injectable } from '@nestjs/common';
import { getChunkRecords } from '@project-management-system/backend-utils';
import { CommonResponseModel, CreateDlMasterReqDto, CreatePaRequestListDto, PaginationDto, ProductionPlanModel, SendEmailReqModel, SendOptions } from '@project-management-system/shared-models';
import { ErrorResponse } from 'packages/libs/backend-utils/src/models/global-res-object';
import { DataSource, In, IsNull, Not } from 'typeorm';
import { uuid } from 'uuidv4';
import { GenericTransactionManager } from '../../typeorm-transactions';
import { GSuiteMailerService } from '../__common__/mailer/g-sute-mailer.service';
import { DlModuleAdapter } from './dl-module.adapter';
import { DLMasterEntity } from './entities/dl-master.entity';
import { ProductionPlanEntity } from './entities/dl-production-plan.entity';
import { DLWhDateChangeLogEntity } from './entities/dl-wh-date-change-log.entity';
import { PARequestChildEntity } from './entities/pa-request-child-entity';
import { PARequestListEntity } from './entities/pa-request-list-entity';
import { DLMasterRepository } from './repositories/dl-master.repository';
import { ProductionPlanRepository } from './repositories/dl-production-plan.repository';
@Injectable()
export class DlModuleService {
    constructor(
        private dLMasterRepository: DLMasterRepository,
        private productionPlanRepository: ProductionPlanRepository,
        private dataSource: DataSource,
        private adapter: DlModuleAdapter,
        private mailServer: GSuiteMailerService
    ) {
    }

    async getDlMastersData(): Promise<CommonResponseModel> {
        const details = await this.dLMasterRepository.find()
        if (details)
            return new CommonResponseModel(true, 1, 'data Retrieved', details)
        else
            return new CommonResponseModel(false, 0, 'No data found');
    }

    async saveDlMasterData(req: CreateDlMasterReqDto[]): Promise<CommonResponseModel> {
        const transactionalManager = new GenericTransactionManager(this.dataSource);
        try {
            await transactionalManager.startTransaction();
            const fileUid = uuid();
            const convertDtoToMasterEntity = this.adapter.convertDtoToMasterEntity(req, fileUid);
            await transactionalManager.getRepository(DLMasterEntity).delete({ id: Not(IsNull()) })
            await transactionalManager.getRepository(DLMasterEntity).save(convertDtoToMasterEntity)
            await transactionalManager.completeTransaction();
            return new CommonResponseModel(true, 54544, 'Uploaded Successfully', { fileReferenceId: fileUid })
        } catch (error) {
            await transactionalManager.releaseTransaction();
            console.log(error.message)
            throw new ErrorResponse(65164, error.message)
        }
    }

    async saveDlProductionPlan(req: ProductionPlanModel[]): Promise<CommonResponseModel> {
        const transactionalManager = new GenericTransactionManager(this.dataSource);
        try {
            await transactionalManager.startTransaction();
            const fileUid = uuid();
            const convertDtoToMasterEntity = this.adapter.convertDtoToProductionPlanEntity(req, fileUid);
            const whDateChangedRecords = new Map<string, { prev: Date, current: Date }>();
            const chunkRecords = getChunkRecords(convertDtoToMasterEntity, 200);
            const promises: Array<Promise<void>> = [];
            chunkRecords.forEach(rec => {
                promises.push(this.getWhDateChanges(rec, whDateChangedRecords));
            });
            await Promise.all(promises);
            await this.processWhDateChangeRecords(whDateChangedRecords, convertDtoToMasterEntity[0], transactionalManager)
            await transactionalManager.getRepository(ProductionPlanEntity).save(convertDtoToMasterEntity, { reload: false });
            await transactionalManager.completeTransaction();
            return new CommonResponseModel(true, 54544, 'Uploaded Successfully', { fileReferenceId: fileUid })
        } catch (error) {
            await transactionalManager.releaseTransaction();
            console.log(error.message)
            throw new ErrorResponse(65164, error.message)
        }
    }

    async processWhDateChangeRecords(whDateChangedRecords: Map<string, {
        prev: Date;
        current: Date;
    }>, savedPlanRec: ProductionPlanModel, transactionalManager: GenericTransactionManager) {
        const productionPlanIds = [];
        const changeLog = []
        whDateChangedRecords.forEach((rec, key) => {
            const saveRec = new DLWhDateChangeLogEntity();
            saveRec.companyCode = savedPlanRec.companyCode;
            saveRec.unitCode = savedPlanRec.unitCode;
            saveRec.createdAt = savedPlanRec.createdAt;
            saveRec.createdUser = savedPlanRec.createdUser;
            saveRec.updatedAt = savedPlanRec.updatedAt;
            saveRec.updatedUser = savedPlanRec.updatedUser;
            saveRec.prevWhDate = rec.prev;
            saveRec.currentWhDate = rec.current;
            saveRec.productionPlanId = key;
            changeLog.push(saveRec);
            productionPlanIds.push(key)
        });
        transactionalManager.getRepository(DLWhDateChangeLogEntity).save(changeLog, { reload: false });
        transactionalManager.getRepository(ProductionPlanEntity).update({ productionPlanId: In(productionPlanIds) }, { isWHDateRevised: true });
    }

    async getWhDateChanges(recs: ProductionPlanEntity[], whDateChangeMap: Map<string, { prev: Date, current: Date }>) {
        const planMap = new Map<string, Date>()
        const planIds = []
        recs.forEach(rec => {
            planMap.set(rec.productionPlanId, rec.whDate);
            planIds.push(rec.productionPlanId);
        });
        const dbRecords = await this.productionPlanRepository.find({ select: ['productionPlanId', 'whDate'], where: { productionPlanId: In(planIds) } });

        for (const savedRecord of dbRecords) {
            if (planMap.get(savedRecord.productionPlanId) !== savedRecord.whDate) {
                whDateChangeMap.set(savedRecord.productionPlanId, { prev: savedRecord.whDate, current: planMap.get(savedRecord.productionPlanId) });
            }
        }
    }

    async getProductionPlan(): Promise<CommonResponseModel> {
        try {
            const data = await this.productionPlanRepository.find()
            return new CommonResponseModel(true, 54544, 'Data retrieved Successfully', data)
        } catch (error) {
            throw new ErrorResponse(65164, error.message)
        }
    };




    async savPaRequestList(req: CreatePaRequestListDto[]): Promise<CommonResponseModel> {
        const transactionalManager = new GenericTransactionManager(this.dataSource)
        try {
            await transactionalManager.startTransaction();
            const fileUid = uuid();
            for (const rec of req) {
                const conversion = this.adapter.convertPARequestListDtoToEntity(rec, fileUid)
                const conversionChild = this.adapter.convertPAChildDtoToEntity(rec)
                await transactionalManager.getRepository(PARequestListEntity).save(conversion);
                await transactionalManager.getRepository(PARequestChildEntity).save(conversionChild)
            }
            await transactionalManager.completeTransaction();
            return new CommonResponseModel(true, 6545, "Saved Successfully", { fileReferenceId: fileUid })
        } catch (error) {
            await transactionalManager.releaseTransaction()
            return new CommonResponseModel(false, 5644, error.message)
        }

    }


    async getPARequestList(req: PaginationDto): Promise<CommonResponseModel> {
        const data: any[] = await this.dataSource.getRepository(PARequestListEntity).find({
            select: ['itemCode', 'itemName', 'managementFactory', 'vendor', 'originalOrderPlanNumber', 'requestId', 'requestRequestUser', 'paRequestChildId'], take: req.to, skip: req.from
        })
        const countOfRecords = await this.dataSource.getRepository(PARequestListEntity).count();
        for (const rec of data) {
            rec.countOfRecords = countOfRecords
            const ch = new PARequestListEntity();
            ch.requestId = rec.requestId
            const childData = await this.dataSource.getRepository(PARequestChildEntity).findOne({ where: { paRequestId: ch } })
            rec.DLFabricOrderNumber = childData.dlFabricOrderNumber
            rec.fabricOrderQty = childData.dlFabricOrderQty
            rec.DLColorOrderQty = childData.dlColorOrderQty
        }
        return new CommonResponseModel(true, 65145, "Data Retrieved Successfully", data)
    }

    async triggerMails(req: SendEmailReqModel): Promise<CommonResponseModel> {
        const emailText = `Dear [${req.assignee}],\n\n You have been assigned a new work order that requires your attention and approval.\n\n Work Order Details: \n 📋 Work Order Number: ${req.workOrderNo} \n 💼 Buyer: ${req.buyer} \n 🛠 WorkOrder Type: ${req.workOrderType} \n 📋 Item Number: ${req.itemNo} \n 💼 Assigned By: ${req.currentUser} \n  
        Please review the details and provide your approval by clicking on the following link: [${req.viewUrl}]
        Remarks: ${req.remarks}
        \n\n Thank you, \n ${req.currentUser} \n`;

        const sendOptions = new SendOptions();
        sendOptions.to = req.assignee;
        sendOptions.from = `"Work Order System" <no-reply@shahi.co.in>`;
        sendOptions.text = emailText;
        sendOptions.attachments = [];
        sendOptions.subject = `Work Order Request Wo.No: ${req.workOrderNo}, item:${req.itemNo}, buyer:${req.buyer}, WorkOrder Type: ${req.workOrderType}, category: ${req.woCategory}`;
        await this.mailServer.sendGSuiteMail(sendOptions);
        return new CommonResponseModel(true, 1110, "Emails sent");
    }
}
